import { Link } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import { motion } from 'framer-motion'
import { BookOpen, Download, ArrowRight, BarChart3, Rocket, Users, Target } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'

type SectionRelated = {
  label: string
  to: string
  external?: boolean
}

type SectionChannelRow = {
  name: string
  objective: string
  metrics: string
  notes: string
}

type GuideSection = {
  id: string
  title: string
  description: string
  highlights?: string[]
  related?: SectionRelated[]
  channelTable?: SectionChannelRow[]
}

type StatCard = {
  icon: LucideIcon
  value: string
  label: string
}

const sections: GuideSection[] = [
  {
    id: 'dijital-pazarlama-nedir',
    title: 'Dijital Pazarlama Nedir?',
    description:
      'Dijital pazarlama, markaların ürün ve hizmetlerini internet ve dijital kanallar üzerinden tanıtmak, potansiyel kitleyle ilişki kurmak ve gelir elde etmek için yürüttüğü tüm stratejileri kapsar. TV ve açık hava gibi geleneksel kanallardan farklı olarak dijital pazarlama veri odaklıdır; kullanıcı davranışlarını anlık izleyip kampanyaları bu verilere göre optimize etmeye olanak tanır.',
    highlights: [
      'İlk dijital reklam 1994 yılında banner formatında yayınlandı; bugün programatik, yapay zekâ ve otomasyon gibi teknolojilerle evrildi.',
      'Türkiye’de internet kullanıcılarının %82’si alışveriş öncesi dijital kanallarda araştırma yapıyor.',
      'Dijital pazarlama bütçeleri 2025’te toplam medya yatırımlarının %65’ini oluşturacak (IAB öngörüsü).',
    ],
    related: [
      { label: 'Detaylı yazı: Dijital Pazarlama Nedir?', to: '/blog/dijital-pazarlama-nedir' },
      { label: 'Case study: Thinklyze ile 6 ayda %140 ROAS artışı', to: '/references' },
    ],
  },
  {
    id: 'strateji-adimlari',
    title: 'Başarılı Bir Dijital Pazarlama Stratejisi Nasıl Kurulur?',
    description:
      'Doğru kurgulanan strateji; hedefleri, kitleyi, mesajı ve bütçeyi netleştirerek tüm kanalların uyum içinde çalışmasını sağlar. Aşağıdaki adımlar Thinklyze’ın tüm projelerinde kullandığı temel yol haritasıdır.',
    highlights: [
      'SMART hedefler belirleyin: Örneğin “3 ay içinde web sitesi trafiğini %40 artırmak” gibi ölçülebilir ve gerçekçi hedefler.',
      'Persona geliştirin: Her persona için ihtiyaç, itiraz ve tetikleyicileri belirleyin. Böylece mesajlar daha kişiselleştirilir.',
      'Kanallara göre KPI tanımlayın: SEO için organik trafik, Google Ads için ROAS, sosyal medya için etkileşim oranı gibi.',
      'Dijital varlıkları optimize edin: Web sitesi hız, mobil uyumluluk, SEO altyapısı ve veri katmanlarının kurulumu kritik önemdedir.',
    ],
    related: [
      { label: 'Strateji şablonunu indir (PDF)', to: 'https://thinklyze.com/assets/dijital-pazarlama-strateji-sablonu.pdf', external: true },
      { label: 'Dijital Pazarlama Danışmanlığı', to: '/consulting' },
    ],
  },
  {
    id: 'kanallar',
    title: 'Dijital Pazarlama Kanalları',
    description:
      'Çok kanallı yaklaşım (omnichannel) kullanıcının yolculuğundaki her dokunuşu optimize eder. Her kanalın güçlü olduğu alan ve temel KPI’ları farklıdır.',
    channelTable: [
      { name: 'SEO', objective: 'Organik görünürlük ve sürdürülebilir trafik', metrics: 'Organik ziyaret, SERP sıralaması, dönüşüm', notes: 'Blog içerikleri, teknik optimizasyon ve yetkili backlinkler ile güçlenir.' },
      { name: 'SEM (Google Ads)', objective: 'Hızlı dönüşüm, talep yakalama', metrics: 'ROAS, CPA, kalite puanı', notes: 'Arama, görüntülü, Shopping ve YouTube kampanyaları.' },
      { name: 'Sosyal Medya Reklamları', objective: 'Farkındalık & remarketing', metrics: 'Etkileşim, CPM, view-through dönüşüm', notes: 'Meta, TikTok, LinkedIn ve Twitter; kreatif test süreçleri kritik.' },
      { name: 'E-posta & Otomasyon', objective: 'Sadakat & gelir artışı', metrics: 'Açılma ve tıklanma oranı, müşteri ömrü değeri', notes: 'Trigger bazlı otomasyonlar, segmentasyon ve kişiselleştirme.' },
      { name: 'İçerik Pazarlaması', objective: 'Otorite ve organik talep yaratma', metrics: 'Okuma süresi, geri dönüş bağlantıları, içerik ROI', notes: 'Rehberler, e-kitaplar, webinarlar; SEO ile entegre çalışmalı.' },
      { name: 'Analitik ve Veri', objective: 'Karar desteği & optimizasyon', metrics: 'GA4, BigQuery, dashboard KPI\'lar', notes: 'Doğru ölçüm olmadan optimizasyon yapılamaz – GTM, piksel ve sunucu tarafı etiketleme şart.' },
    ],
    related: [
      { label: 'Dijital Pazarlama Kanalları Yazısı', to: '/blog/dijital-pazarlama-kanallari' },
      { label: 'Google Ads Hizmetleri', to: '/services/google-ads' },
      { label: 'Meta Ads Yönetimi', to: '/services/meta-ads' },
    ],
  },
  {
    id: 'olcum-ve-analitik',
    title: 'Ölçümleme, Analitik ve Otomasyon',
    description:
      'Veri katmanı güçlü olmayan hiçbir dijital pazarlama stratejisi sürdürülebilir değildir. Ölçülemeyen şey iyileştirilemez. Thinklyze projelerinde veri altyapısı ilk sprintte tamamlanır.',
    highlights: [
      'GA4, Meta Pixel ve sunucu tarafı etiketleme ile müşteri yolculuğunu uçtan uca izleyin.',
      'Looker Studio, Supermetrics veya kendi dashboard’unuzla paydaşlara şeffaf rapor sunun.',
      'Marketing automation (HubSpot, Ortto, Klaviyo) ile tetik bazlı e-posta/SMS akışları kurgulayın.',
      'Yapay zekâ destekli bid yönetimi ve kreatif testleri (Google Ads, Meta Advantage+) verimliliği artırır.',
    ],
    related: [
      { label: 'GA4 & Meta Pixel İzleme Kılavuzu', to: '/blog/google-analytics-4-yeni-ozellikler-ve-gecis-rehberi' },
      { label: 'Marketing Tools', to: '/marketing-tools' },
    ],
  },
  {
    id: 'trendler-ve-gelecek',
    title: '2025 ve Sonrası İçin Dijital Pazarlama Trendleri',
    description:
      'Yapay zekâ, gizlilik regülasyonları ve ilk taraf veri stratejileri önümüzdeki dönemin en kritik üç konusu. Türkiye pazarında öne çıkacak başlıklar:',
    highlights: [
      'Sunucu tarafı ölçüm (CAPI, S2S tracking) zorunluluk hâline geliyor. Çerezsiz dünyada veri kayıplarını telafi edecek altyapı kurun.',
      'Kısa video ve UGC içerikleri reklam kreatiflerinde default hâline geldi. Performans kanallarıyla entegrasyon şart.',
      'Yapay zekâ destekli içerik ve kampanya üretimi (ChatGPT, Claude, Gemini) kreatif süreci hızlandırıyor; fakat marka tonu için insan dokunuşu kritik.',
      'Performans ve marka kampanyalarının birleştiği “full funnel” stratejiler büyümeyi hızlandırıyor.',
    ],
    related: [
      { label: 'Thinklyze UGC & Reels Örnekleri', to: '/blog/kreatif-kazandirir-dusuk-butceyle-bile-is-yapan-ugc-ve-reels-reklam-ornekleri' },
      { label: 'Mentorluk Programı', to: '/services/mentorluk' },
    ],
  },
]

const stats: StatCard[] = [
  { icon: BarChart3, value: '68%', label: 'Reklamdan gelen geliri veri odaklı optimizasyonla artıran Thinklyze müşterileri' },
  { icon: Rocket, value: '9 ülkede', label: 'Aktif Thinklyze kampanya yönetimi' },
  { icon: Users, value: '15 kişilik', label: 'Çok disiplinli büyüme ekibi' },
  { icon: Target, value: '4.5x', label: '2024 ortalama ROAS artışı' },
]

const faqItems = [
  {
    question: 'Dijital pazarlama bütçesi nasıl planlanır?',
    answer:
      'İlk olarak gelir hedefinizi belirleyin ve pazarlama/ciro oranını (ör. %12) tanımlayın. Ardından kanallara göre tahmini CPA/ROAS değerleriyle bütçeyi dağıtın. Thinklyze, 90 günlük sprint çerçevesinde bütçeyi aylık olarak revize eder.',
  },
  {
    question: 'Dijital pazarlama ajansı ile çalışmanın avantajı nedir?',
    answer:
      'Uzman ekiplerle hem strateji hem uygulama katmanı tek elde toplanır, kampanyalar daha hızlı optimize edilir ve ROI şeffaf şekilde raporlanır. Ayrıca yeni teknolojilere (sunucu tarafı takip, otomasyon) erişim kolaylaşır.',
  },
  {
    question: 'Hangi sektörlerde dijital pazarlama daha etkilidir?',
    answer:
      'E-ticaret, SaaS, eğitim, finans, sağlık turizmi gibi yüksek rekabetli alanlarda dijital pazarlama büyümenin ana motorudur. Ancak B2B endüstriyel pazarlarda bile doğru içerik ve otomasyonla yüksek potansiyel vardır.',
  },
  {
    question: 'SEO mu yoksa reklam mı daha önemlidir?',
    answer:
      'Her ikisi de funnel’ın farklı noktalarını hedefler. SEO uzun vadede sürdürülebilir trafik sağlarken reklam kanalları talebi hızlı yakalar. Thinklyze yaklaşımı, organik ve ücretli kanalları entegre ederek toplam büyümeyi maksimize etmektir.',
  },
]

const articleSchema = {
  '@context': 'https://schema.org',
  '@type': 'Article',
  headline: 'Dijital Pazarlama Rehberi (2025)',
  description:
    'Dijital pazarlama nedir, hangi kanallar kullanılmalı, strateji nasıl oluşturulur ve 2025 trendleri neler? Thinklyze büyüme ekibinin hazırladığı kapsamlı rehber.',
  author: {
    '@type': 'Organization',
    name: 'Thinklyze',
  },
  publisher: {
    '@type': 'Organization',
    name: 'Thinklyze',
    logo: {
      '@type': 'ImageObject',
      url: 'https://thinklyze.com/images/logo_siyah.webp',
    },
  },
  url: 'https://thinklyze.com/blog/dijital-pazarlama-rehberi',
  mainEntityOfPage: 'https://thinklyze.com/blog/dijital-pazarlama-rehberi',
}

const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqItems.map((item) => ({
    '@type': 'Question',
    name: item.question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: item.answer,
    },
  })),
}

const DigitalMarketingGuide = () => {
  return (
    <div className="pt-16 bg-[#f9f9f9] relative">
      <Helmet>
        <title>Dijital Pazarlama Rehberi (2025) | Thinklyze</title>
        <meta
          name="description"
          content="Dijital pazarlama nedir, hangi kanallar kullanılır, strateji nasıl oluşturulur ve 2025 trendleri neler? Thinklyze'ın kapsamlı dijital pazarlama rehberini inceleyin."
        />
        <link rel="canonical" href="https://thinklyze.com/blog/dijital-pazarlama-rehberi" />
        <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      <div className="absolute inset-0 bg-[linear-gradient(to_right,#e5e7eb_1px,transparent_1px),linear-gradient(to_bottom,#e5e7eb_1px,transparent_1px)] bg-[size:80px_80px] [mask-image:radial-gradient(ellipse_60%_40%_at_50%_10%,#000_20%,transparent_70%)] opacity-40 pointer-events-none -z-10" aria-hidden />

      <section className="py-24 relative">
        <div className="max-w-[1200px] mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <span className="inline-flex items-center gap-2 text-[13px] font-medium text-[#666] bg-white px-4 py-1.5 rounded-full border border-gray-200 mb-6 shadow-sm">
              <BookOpen className="w-4 h-4" />
              Kapsamlı Rehber
            </span>
            <h1 className="text-[28px] sm:text-[36px] md:text-[44px] font-bold text-[#111] leading-tight mb-6">
              Dijital Pazarlama Rehberi (2025): Strateji, Kanallar ve Ölçümleme
            </h1>
            <p className="text-lg md:text-xl text-[#555] max-w-3xl mx-auto mb-8 leading-relaxed">
              Bu rehber, Thinklyze’ın butik büyüme ekibinin farklı sektörlerde yönettiği kampanyalardan süzülmüş içgörülerle hazırlandı. Stratejiden kanallara, ölçümlemeden 2025 trendlerine kadar ihtiyacınız olan tüm bilgiyi tek sayfada toplayın.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a
                href="https://thinklyze.com/assets/dijital-pazarlama-checklist.pdf"
                className="inline-flex items-center gap-2 bg-black text-white px-6 py-3 rounded-full text-sm font-semibold hover:bg-gray-800 transition"
                target="_blank"
                rel="noopener noreferrer"
              >
                <Download className="w-4 h-4" />
                Rehberi PDF Olarak İndir
              </a>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 text-gray-800 px-4 py-3 rounded-full border border-gray-300 hover:bg-gray-100 transition text-sm bg-white shadow-sm"
              >
                Uzmanla Görüş <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="relative max-w-6xl mx-auto px-6 py-10 grid grid-cols-1 lg:grid-cols-[320px_minmax(0,1fr)] gap-10">
        <aside className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 sticky top-24 h-fit">
          <h2 className="text-sm font-semibold text-gray-900 uppercase tracking-wide">İçindekiler</h2>
          <ul className="space-y-3 mt-4 text-sm text-gray-600">
            {sections.map((section) => (
              <li key={section.id}>
                <a href={`#${section.id}`} className="hover:text-gray-900 transition">
                  {section.title}
                </a>
              </li>
            ))}
            <li>
              <a href="#faq" className="hover:text-gray-900 transition">
                Sık Sorulan Sorular
              </a>
            </li>
          </ul>
          <div className="mt-8 p-4 rounded-xl bg-gray-50 border border-dashed border-gray-200 text-sm text-gray-600">
            <strong className="block text-gray-900 mb-2">İpucu:</strong>
            İlgili başlıklardaki ayrıntılı blog yazılarına bağlantıları görebilirsiniz. Rehberinizi cluster içerikleriyle güçlendirmeyi unutmayın.
          </div>
        </aside>

        <div className="space-y-16">
          <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {stats.map((item, index) => (
              <div key={index} className="relative group">
                <div className="absolute inset-0 rounded-[26px] bg-gradient-to-br from-gray-100 via-white to-gray-100 opacity-70 blur-lg group-hover:opacity-90 transition-opacity duration-500" aria-hidden />
                <div className="relative h-full rounded-[22px] bg-white/95 backdrop-blur-sm border border-gray-100/80 shadow-[0_18px_38px_rgba(15,23,42,0.08)] hover:shadow-[0_24px_52px_rgba(15,23,42,0.14)] transition-all duration-300 p-6 flex flex-col items-center text-center gap-4 min-h-[260px]">
                  <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-gray-900 text-white shadow-lg shadow-gray-900/20">
                    <item.icon className="w-7 h-7" />
                  </div>
                  <p className="text-[34px] font-semibold text-gray-900 tracking-tight leading-none">{item.value}</p>
                  <p className="text-sm leading-relaxed text-gray-600 text-balance">
                    {item.label}
                  </p>
                  <div className="mt-auto h-[3px] w-16 bg-gradient-to-r from-gray-900/30 via-gray-900/10 to-transparent rounded-full group-hover:from-gray-900/50 group-hover:via-gray-900/20 transition-colors" aria-hidden />
                </div>
              </div>
            ))}
          </section>

          {sections.map((section) => (
            <section key={section.id} id={section.id} className="bg-white rounded-3xl shadow-sm border border-gray-100 p-8 md:p-10">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">{section.title}</h2>
              <p className="text-gray-700 leading-relaxed mb-6">{section.description}</p>

              {section.highlights && (
                <ul className="list-disc pl-6 space-y-3 text-gray-700">
                  {section.highlights.map((item, idx) => (
                    <li key={idx}>{item}</li>
                  ))}
                </ul>
              )}

              {section.channelTable && (
                <div className="mt-6 overflow-hidden rounded-2xl border border-gray-100">
                  <table className="min-w-full divide-y divide-gray-100 text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left font-semibold text-gray-900">Kanal</th>
                        <th className="px-4 py-3 text-left font-semibold text-gray-900">Amaç</th>
                        <th className="px-4 py-3 text-left font-semibold text-gray-900">Temel KPI’lar</th>
                        <th className="px-4 py-3 text-left font-semibold text-gray-900">Notlar</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-100 text-gray-700">
                      {section.channelTable.map((row, idx) => (
                        <tr key={idx} className="hover:bg-gray-50">
                          <td className="px-4 py-3 font-medium text-gray-900">{row.name}</td>
                          <td className="px-4 py-3">{row.objective}</td>
                          <td className="px-4 py-3">{row.metrics}</td>
                          <td className="px-4 py-3">{row.notes}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {section.related && (
                <div className="mt-6 flex flex-wrap gap-3">
                  {section.related.map((item, idx) => (
                    item.external ? (
                      <a
                        key={idx}
                        href={item.to}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 transition px-4 py-2 rounded-full"
                      >
                        {item.label} <ArrowRight className="w-4 h-4" />
                      </a>
                    ) : (
                      <Link
                        key={idx}
                        to={item.to}
                        className="inline-flex items-center gap-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 transition px-4 py-2 rounded-full"
                      >
                        {item.label} <ArrowRight className="w-4 h-4" />
                      </Link>
                    )
                  ))}
                </div>
              )}
            </section>
          ))}

          <section id="faq" className="bg-white rounded-3xl shadow-sm border border-gray-100 p-8 md:p-10">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6">Sık Sorulan Sorular</h2>
            <div className="space-y-6">
              {faqItems.map((item, idx) => (
                <div key={idx} className="border border-gray-100 rounded-2xl p-6 hover:border-gray-200 transition">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{item.question}</h3>
                  <p className="text-gray-700 leading-relaxed">{item.answer}</p>
                </div>
              ))}
            </div>
          </section>

          <section className="bg-gray-900 text-white rounded-3xl px-8 py-10 flex flex-col md:flex-row items-start md:items-center gap-6">
            <div className="flex-1 space-y-3">
              <p className="uppercase tracking-wide text-sm text-gray-300">Thinklyze ile Büyüyün</p>
              <h2 className="text-2xl md:text-3xl font-bold">Veri Odaklı Dijital Pazarlama Stratejinizi Birlikte Tasarlayalım</h2>
              <p className="text-gray-300 leading-relaxed">
                SEO, performans reklamcılığı, içerik ve otomasyon ekiplerimizle tek çatı altında çalışarak ölçülebilir büyüme hedeflerinize ulaşın. 15 dakikalık ücretsiz keşif görüşmesi planlayın.
              </p>
            </div>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 bg-white text-gray-900 px-5 py-3 rounded-full font-semibold hover:bg-gray-100 transition"
            >
              Görüşme Planla <ArrowRight className="w-4 h-4" />
            </Link>
          </section>
        </div>
      </section>
    </div>
  )
}

export default DigitalMarketingGuide




